import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuditTrail } from '../../../services/auditTrailApi';
import type { AuditTrailItem, FetchAuditTrailParams } from './auditTrailTypes';
import {
  logActivityFilterApplied,
  logActivityFilterCleared,
  logActivitySearch,
} from '../../../../audit/activityAudit';

export interface AuditTrailFilters {
  module: string;
  eventType: string;
  severities: string[];
  actorId: string;
  search: string;
  dateFrom: Date | undefined;
  dateTo: Date | undefined;
}

const DEFAULT_FILTERS: AuditTrailFilters = {
  module: '',
  eventType: '',
  severities: [],
  actorId: '',
  search: '',
  dateFrom: undefined,
  dateTo: undefined,
};

function toIsoStart(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  return x.toISOString();
}

function toIsoEnd(d: Date): string {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return x.toISOString();
}

/** Map UI severity options to API-compatible values (stored mostly as INFO/WARN/ERR). */
function buildSeverityParam(severities: string[]): string | undefined {
  if (severities.length === 0) return undefined;
  const mapped = severities.map((s) => {
    const upper = s.toUpperCase();
    if (upper === 'ERROR') return 'ERR';
    if (upper === 'WARNING') return 'WARN';
    return upper;
  });
  return Array.from(new Set(mapped)).join(',');
}

export function useAuditTrailQuery(projectId: number | undefined, projectName?: string | null) {
  const [filters, setFilters] = useState<AuditTrailFilters>(DEFAULT_FILTERS);
  const [debouncedActorId, setDebouncedActorId] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [items, setItems] = useState<AuditTrailItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setPage(1);
  }, [projectId]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedActorId(filters.actorId.trim()), 300);
    return () => window.clearTimeout(t);
  }, [filters.actorId]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(filters.search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [filters.search]);

  useEffect(() => {
    if (!debouncedSearch) return;
    logActivitySearch('audit-trail', debouncedSearch, projectId, projectName);
  }, [debouncedSearch, projectId, projectName]);

  const prevFilterKeyRef = useRef('');

  useEffect(() => {
    const filterPayload = {
      module: filters.module,
      eventType: filters.eventType,
      severities: filters.severities,
      actorId: debouncedActorId,
      dateFrom: filters.dateFrom?.toISOString() ?? null,
      dateTo: filters.dateTo?.toISOString() ?? null,
    };
    const key = JSON.stringify(filterPayload);
    const hasFilter = Boolean(
      filters.module ||
        filters.eventType ||
        filters.severities.length ||
        debouncedActorId ||
        filters.dateFrom ||
        filters.dateTo
    );
    if (!hasFilter || key === prevFilterKeyRef.current) return;
    prevFilterKeyRef.current = key;
    logActivityFilterApplied('audit-trail', filterPayload, projectId, projectName);
  }, [
    filters.module,
    filters.eventType,
    filters.severities,
    filters.dateFrom,
    filters.dateTo,
    debouncedActorId,
    projectId,
    projectName,
  ]);

  const updateFilters = useCallback((patch: Partial<AuditTrailFilters>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    logActivityFilterCleared('audit-trail', projectId, projectName);
    setFilters(DEFAULT_FILTERS);
    setDebouncedActorId('');
    setDebouncedSearch('');
    setPage(1);
  }, [projectId, projectName]);

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);
    setUnavailable(false);

    const offset = (page - 1) * itemsPerPage;
    const params: FetchAuditTrailParams = {
      limit: itemsPerPage,
      offset,
      signal: controller.signal,
    };

    if (projectId != null && Number.isFinite(projectId)) {
      params.project_id = projectId;
    }

    if (filters.module) params.module = filters.module;
    if (filters.eventType) params.event_type = filters.eventType;
    const severity = buildSeverityParam(filters.severities);
    if (severity) params.severity = severity;
    if (debouncedActorId) params.actor_id = debouncedActorId;
    if (debouncedSearch) params.search = debouncedSearch;
    if (filters.dateFrom) params.created_from = toIsoStart(filters.dateFrom);
    if (filters.dateTo) params.created_to = toIsoEnd(filters.dateTo);

    const result = await fetchAuditTrail(params);

    if (controller.signal.aborted) return;

    setLoading(false);

    if (result.ok) {
      setItems(result.data.items);
      setTotal(result.data.total);
      return;
    }

    if (result.error === 'Request cancelled') return;

    setItems([]);
    setTotal(0);
    setError(result.error);
    setUnavailable(Boolean(result.unavailable));
  }, [
    projectId,
    page,
    itemsPerPage,
    filters.module,
    filters.eventType,
    filters.severities,
    filters.dateFrom,
    filters.dateTo,
    debouncedActorId,
    debouncedSearch,
  ]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return {
    filters,
    updateFilters,
    resetFilters,
    page,
    setPage,
    itemsPerPage,
    setItemsPerPage,
    items,
    total,
    loading,
    error,
    unavailable,
    reload: load,
  };
}
